import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../components/AuthContext';
import { 
  FaPlus, 
  FaTrash, 
  FaSearch, 
  FaEdit, 
  FaEye, 
  FaTruck, 
  FaUser, 
  FaPhone, 
  FaWarehouse, 
  FaFileAlt, 
  FaCheck, 
  FaTimes, 
  FaBan,
  FaPrint,
  FaFilter,
  FaSync
} from 'react-icons/fa';

const PackingSlip = () => {
  const { user } = useContext(AuthContext);
  
  // Main data states
  const [packingSlips, setPackingSlips] = useState([]);
  const [availableDeliveryOrders, setAvailableDeliveryOrders] = useState([]);
  const [godowns, setGodowns] = useState([]);
  
  // UI states
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [itemsPerPage] = useState(20);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeliveryOrderModal, setShowDeliveryOrderModal] = useState(false);
  const [selectedPackingSlip, setSelectedPackingSlip] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    deliveryOrderId: '',
    vehicleNumber: '',
    contactPersonName: '',
    contactPersonPhone: '',
    remarks: '',
    godownId: '',
    items: []
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [selectedDeliveryOrder, setSelectedDeliveryOrder] = useState(null);
  const [availableItems, setAvailableItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [deliveryOrderSearch, setDeliveryOrderSearch] = useState('');

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  // Load data on component mount and when filters change
  useEffect(() => {
    fetchPackingSlips();
  }, [currentPage, searchTerm, statusFilter]);

  useEffect(() => {
    loadGodowns();
  }, []);

  // Fetch packing slips
  const fetchPackingSlips = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage,
        companyId: user.company._id,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { status: statusFilter })
      });

      const response = await fetch(`${API_BASE}/packing-slips?${params}`);
      const data = await response.json();

      if (data.success) {
        setPackingSlips(data.data);
        setTotalPages(data.pagination.totalPages);
        setTotalCount(data.pagination.totalCount);
      } else {
        console.error('Failed to fetch packing slips:', data.error);
      }
    } catch (error) {
      console.error('Error fetching packing slips:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load available delivery orders for packing slip creation
  const loadAvailableDeliveryOrders = async () => {
    try {
      const params = new URLSearchParams({
        companyId: user.company._id,
        limit: 100,
        ...(deliveryOrderSearch && { search: deliveryOrderSearch })
      });

      const response = await fetch(`${API_BASE}/packing-slips/available-delivery-orders?${params}`);
      const data = await response.json();

      if (data.success) {
        setAvailableDeliveryOrders(data.data);
      }
    } catch (error) {
      console.error('Error loading delivery orders:', error);
    }
  };

  // Load godowns
  const loadGodowns = async () => {
    try {
      const params = new URLSearchParams({
        companyId: user.company._id
      });

      const response = await fetch(`${API_BASE}/packing-slips/godowns?${params}`);
      const data = await response.json();

      if (data.success) {
        setGodowns(data.data);
      }
    } catch (error) {
      console.error('Error loading godowns:', error);
    }
  };

  // Initialize form for creation
  const initializeCreateForm = () => {
    setFormData({
      deliveryOrderId: '',
      vehicleNumber: '',
      contactPersonName: '',
      contactPersonPhone: '',
      remarks: '',
      godownId: '',
      items: []
    });
    setFormErrors({});
    setSelectedDeliveryOrder(null);
    setAvailableItems([]);
    setDeliveryOrderSearch('');
    loadAvailableDeliveryOrders();
    setShowDeliveryOrderModal(true);
  };

  // Handle delivery order selection
  const handleDeliveryOrderSelect = async (deliveryOrder) => {
    setSelectedDeliveryOrder(deliveryOrder);
    setFormData(prev => ({ ...prev, deliveryOrderId: deliveryOrder._id }));
    setShowDeliveryOrderModal(false);

    // Load available items for this delivery order
    try {
      const params = new URLSearchParams({
        companyId: user.company._id
      });

      const response = await fetch(`${API_BASE}/packing-slips/delivery-order/${deliveryOrder._id}/available-items?${params}`);
      const data = await response.json();

      if (data.success) {
        setAvailableItems(data.data.items);
        // Initialize items with available quantities
        const initialItems = data.data.items
          .filter(item => item.isAvailable)
          .map(item => ({
            itemId: item.itemId,
            Name: item.Name,
            quantity: 0,
            maxQuantity: item.availableQuantity,
            units: item.units,
            packingNotes: ''
          }));
        setFormData(prev => ({ ...prev, items: initialItems }));
        setShowCreateModal(true);
      }
    } catch (error) {
      console.error('Error loading available items:', error);
    }
  };

  // Update item quantity with validation
  const updateItemQuantity = (itemIndex, quantity) => {
    const numQuantity = parseInt(quantity) || 0;
    const maxQuantity = formData.items[itemIndex].maxQuantity;

    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, idx) => 
        idx === itemIndex 
          ? { 
              ...item, 
              quantity: Math.min(Math.max(0, numQuantity), maxQuantity)
            }
          : item
      )
    }));
  };

  // Update item packing notes
  const updateItemPackingNotes = (itemIndex, notes) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, idx) => 
        idx === itemIndex 
          ? { ...item, packingNotes: notes }
          : item
      )
    }));
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.deliveryOrderId) errors.deliveryOrderId = 'Delivery order is required';
    if (!formData.vehicleNumber.trim()) errors.vehicleNumber = 'Vehicle number is required';
    if (!formData.contactPersonName.trim()) errors.contactPersonName = 'Contact person name is required';
    if (!formData.contactPersonPhone.trim()) {
      errors.contactPersonPhone = 'Contact person phone is required';
    } else if (!/^[0-9]{10}$/.test(formData.contactPersonPhone.trim())) {
      errors.contactPersonPhone = 'Please enter a valid 10-digit phone number';
    }
    if (!formData.godownId) errors.godownId = 'Godown is required';

    // Validate items
    const itemsWithQuantity = formData.items.filter(item => item.quantity > 0);
    if (itemsWithQuantity.length === 0) {
      errors.items = 'At least one item with quantity > 0 is required';
    }

    return errors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setSubmitting(true);
    try {
      // Filter items with quantity > 0
      const itemsToSubmit = formData.items
        .filter(item => item.quantity > 0)
        .map(({ maxQuantity, ...item }) => item);

      const payload = {
        ...formData,
        items: itemsToSubmit,
        createdBy: user._id
      };

      const response = await fetch(`${API_BASE}/packing-slips`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        setShowCreateModal(false);
        fetchPackingSlips();
        alert('Packing slip created successfully!');
      } else {
        setFormErrors({ submit: data.error });
      }
    } catch (error) {
      setFormErrors({ submit: 'Failed to create packing slip' });
      console.error('Error creating packing slip:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle status update
  const handleStatusUpdate = async (packingSlip, newStatus) => {
    try {
      const response = await fetch(`${API_BASE}/packing-slips/${packingSlip._id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: newStatus,
          updatedBy: user._id
        })
      });

      const data = await response.json();

      if (data.success) {
        fetchPackingSlips();
        alert(`Packing slip ${newStatus.toLowerCase()} successfully!`);
      } else {
        alert('Failed to update status: ' + data.error);
      }
    } catch (error) {
      alert('Error updating status');
      console.error('Error updating status:', error);
    }
  };

  // Handle delete
  const handleDelete = async (packingSlip) => {
    if (!window.confirm('Are you sure you want to delete this packing slip?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/packing-slips/${packingSlip._id}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (data.success) {
        fetchPackingSlips();
        alert('Packing slip deleted successfully!');
      } else {
        alert('Failed to delete packing slip: ' + data.error);
      }
    } catch (error) {
      alert('Error deleting packing slip');
      console.error('Error deleting packing slip:', error);
    }
  };

  // Handle print
  const handlePrint = (packingSlip) => {
    const printWindow = window.open(`/packing-slip/${packingSlip.packingSlipNumber}`, '_blank');
    if (printWindow) {
      printWindow.focus();
    }
  };

  // Get status color classes
  const getStatusClasses = (status) => {
    const classes = {
      'Draft': 'bg-gray-100 text-gray-800',
      'Issued': 'bg-blue-100 text-blue-800',
      'Completed': 'bg-green-100 text-green-800',
      'Cancelled': 'bg-red-100 text-red-800'
    };
    return classes[status] || 'bg-gray-100 text-gray-800';
  };

  // Reset filters
  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setCurrentPage(1);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <FaFileAlt className="mr-3 text-blue-600" />
          Packing Slips Management
        </h1>
        <p className="text-gray-600 mt-2">Manage and track packing slips for delivery orders</p>
      </div>

      {/* Filters and Actions */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <FaSearch className="absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Search packing slips..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status Filter</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Statuses</option>
              <option value="Draft">Draft</option>
              <option value="Issued">Issued</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>

          <div className="flex gap-2">
            <button
              onClick={resetFilters}
              className="flex items-center px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <FaSync className="mr-2" />
              Reset
            </button>
          </div>

          <div>
            <button
              onClick={initializeCreateForm}
              className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <FaPlus className="mr-2" />
              Create Packing Slip
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FaFileAlt className="text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Total Packing Slips</p>
              <p className="text-2xl font-semibold text-gray-900">{totalCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Packing Slips Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Packing Slip
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Delivery Order
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact Person
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vehicle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created Date
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    </div>
                  </td>
                </tr>
              ) : packingSlips.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                    No packing slips found
                  </td>
                </tr>
              ) : (
                packingSlips.map((slip) => (
                  <tr key={slip._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {slip.packingSlipNumber}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {slip.deliveryOrder?.doNumber || slip.deliveryOrder?.Manual_dO_Number}
                      </div>
                      {slip.deliveryOrder?.recipient && (
                        <div className="text-xs text-gray-500">
                          {slip.deliveryOrder.recipient.name}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {slip.contactPersonName}
                      </div>
                      <div className="text-xs text-gray-500">
                        {slip.contactPersonPhone}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        <FaTruck className="mr-1" />
                        {slip.vehicleNumber}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusClasses(slip.status)}`}>
                        {slip.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(slip.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                      <div className="flex justify-center space-x-2">
                        <button
                          onClick={() => {
                            setSelectedPackingSlip(slip);
                            setShowViewModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Details"
                        >
                          <FaEye />
                        </button>
                        
                        <button
                          onClick={() => handlePrint(slip)}
                          className="text-green-600 hover:text-green-900"
                          title="Print"
                        >
                          <FaPrint />
                        </button>

                        {slip.status === 'Draft' && (
                          <button
                            onClick={() => handleStatusUpdate(slip, 'Issued')}
                            className="text-blue-600 hover:text-blue-900"
                            title="Mark as Issued"
                          >
                            <FaCheck />
                          </button>
                        )}

                        {slip.status === 'Issued' && (
                          <button
                            onClick={() => handleStatusUpdate(slip, 'Completed')}
                            className="text-green-600 hover:text-green-900"
                            title="Mark as Completed"
                          >
                            <FaCheck />
                          </button>
                        )}

                        {slip.status === 'Draft' && (
                          <button
                            onClick={() => handleDelete(slip)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex justify-between flex-1 sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage <= 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage >= totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing page {currentPage} of {totalPages} ({totalCount} total)
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage <= 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                    const pageNum = Math.max(1, Math.min(totalPages, currentPage - 2 + idx));
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === pageNum
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage >= totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delivery Order Selection Modal */}
      {showDeliveryOrderModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Select Delivery Order</h3>
              
              {/* Search */}
              <div className="mb-4">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-3 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search delivery orders..."
                    value={deliveryOrderSearch}
                    onChange={(e) => {
                      setDeliveryOrderSearch(e.target.value);
                      loadAvailableDeliveryOrders();
                    }}
                    className="pl-10 w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Delivery Orders List */}
              <div className="max-h-96 overflow-y-auto">
                {availableDeliveryOrders.length === 0 ? (
                  <p className="text-center text-gray-500 py-4">No available delivery orders found</p>
                ) : (
                  <div className="space-y-2">
                    {availableDeliveryOrders.map((order) => (
                      <div
                        key={order._id}
                        onClick={() => handleDeliveryOrderSelect(order)}
                        className="p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-colors"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-medium text-gray-900">
                              {order.doNumber || order.Manual_dO_Number}
                            </h4>
                            <p className="text-sm text-gray-600">
                              {order.recipient?.name || 'Unknown Recipient'}
                            </p>
                            <p className="text-xs text-gray-500">
                              {order.items?.length} items available
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-500">
                              Created: {new Date(order.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-4">
                <button
                  onClick={() => setShowDeliveryOrderModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Packing Slip Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 xl:w-2/3 shadow-lg rounded-md bg-white">
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <h3 className="text-lg font-medium text-gray-900">Create Packing Slip</h3>
                {selectedDeliveryOrder && (
                  <p className="text-sm text-gray-600 mt-1">
                    For Delivery Order: {selectedDeliveryOrder.doNumber || selectedDeliveryOrder.Manual_dO_Number}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {/* Vehicle Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vehicle Number *
                  </label>
                  <input
                    type="text"
                    value={formData.vehicleNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, vehicleNumber: e.target.value }))}
                    className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors.vehicleNumber ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter vehicle number"
                  />
                  {formErrors.vehicleNumber && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.vehicleNumber}</p>
                  )}
                </div>

                {/* Godown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Godown *
                  </label>
                  <select
                    value={formData.godownId}
                    onChange={(e) => setFormData(prev => ({ ...prev, godownId: e.target.value }))}
                    className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors.godownId ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select Godown</option>
                    {godowns.map((godown) => (
                      <option key={godown._id} value={godown._id}>
                        {godown.Name}
                      </option>
                    ))}
                  </select>
                  {formErrors.godownId && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.godownId}</p>
                  )}
                </div>

                {/* Contact Person Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Person Name *
                  </label>
                  <input
                    type="text"
                    value={formData.contactPersonName}
                    onChange={(e) => setFormData(prev => ({ ...prev, contactPersonName: e.target.value }))}
                    className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors.contactPersonName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter contact person name"
                  />
                  {formErrors.contactPersonName && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.contactPersonName}</p>
                  )}
                </div>

                {/* Contact Person Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Person Phone *
                  </label>
                  <input
                    type="tel"
                    value={formData.contactPersonPhone}
                    onChange={(e) => setFormData(prev => ({ ...prev, contactPersonPhone: e.target.value }))}
                    className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors.contactPersonPhone ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter 10-digit phone number"
                    maxLength="10"
                  />
                  {formErrors.contactPersonPhone && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.contactPersonPhone}</p>
                  )}
                </div>
              </div>

              {/* Remarks */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Remarks
                </label>
                <textarea
                  value={formData.remarks}
                  onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="2"
                  placeholder="Optional remarks"
                />
              </div>

              {/* Items Section */}
              <div className="mb-4">
                <h4 className="text-md font-medium text-gray-900 mb-2">Items to Pack</h4>
                {formData.items.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No available items found</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Item Name</th>
                          <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Available</th>
                          <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Pack Qty</th>
                          <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Units</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Packing Notes</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {formData.items.map((item, index) => (
                          <tr key={item.itemId}>
                            <td className="px-4 py-2">
                              <div className="text-sm font-medium text-gray-900">{item.Name}</div>
                            </td>
                            <td className="px-4 py-2 text-center">
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                {item.maxQuantity}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-center">
                              <input
                                type="number"
                                min="0"
                                max={item.maxQuantity}
                                value={item.quantity}
                                onChange={(e) => updateItemQuantity(index, e.target.value)}
                                className="w-20 border border-gray-300 rounded-md px-2 py-1 text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </td>
                            <td className="px-4 py-2 text-center text-sm text-gray-500">
                              {item.units}
                            </td>
                            <td className="px-4 py-2">
                              <input
                                type="text"
                                value={item.packingNotes}
                                onChange={(e) => updateItemPackingNotes(index, e.target.value)}
                                className="w-full border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Optional notes"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {formErrors.items && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.items}</p>
                )}
              </div>

              {formErrors.submit && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                  {formErrors.submit}
                </div>
              )}

              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <FaPlus className="mr-2" />
                      Create Packing Slip
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Packing Slip Modal */}
      {showViewModal && selectedPackingSlip && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-900">Packing Slip Details</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Packing Slip Number</label>
                <p className="text-sm text-gray-900">{selectedPackingSlip.packingSlipNumber}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusClasses(selectedPackingSlip.status)}`}>
                  {selectedPackingSlip.status}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Vehicle Number</label>
                <p className="text-sm text-gray-900">{selectedPackingSlip.vehicleNumber}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Contact Person</label>
                <p className="text-sm text-gray-900">{selectedPackingSlip.contactPersonName}</p>
                <p className="text-xs text-gray-500">{selectedPackingSlip.contactPersonPhone}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Godown</label>
                <p className="text-sm text-gray-900">{selectedPackingSlip.godown?.Name || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Created Date</label>
                <p className="text-sm text-gray-900">{new Date(selectedPackingSlip.createdAt).toLocaleDateString()}</p>
              </div>
            </div>

            {selectedPackingSlip.remarks && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">Remarks</label>
                <p className="text-sm text-gray-900">{selectedPackingSlip.remarks}</p>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Packed Items</label>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Item Name</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Quantity</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Units</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Packing Notes</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedPackingSlip.items?.map((item, index) => (
                      <tr key={index}>
                        <td className="px-4 py-2 text-sm text-gray-900">{item.Name}</td>
                        <td className="px-4 py-2 text-center text-sm text-gray-900">{item.quantity}</td>
                        <td className="px-4 py-2 text-center text-sm text-gray-900">{item.units}</td>
                        <td className="px-4 py-2 text-sm text-gray-900">{item.packingNotes || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => handlePrint(selectedPackingSlip)}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center"
              >
                <FaPrint className="mr-2" />
                Print
              </button>
              <button
                onClick={() => setShowViewModal(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackingSlip;
